import { MAIN_NATIONS } from '@repo/constants';
import { isNationIncluded } from '@repo/utils';

type OptionalNation = string | null | undefined;

type LogoutModalContent = {
  title: string;
  content: string;
  btnText: string;
  subBtnText: string;
};

type RefundInfoButtonParams = {
  currNation?: OptionalNation;
  nationality: string;
  isUploadRequired: boolean;
  isRefundInfoRegistered: boolean;
  isCashPickup: boolean;
};

type ReceiptInquiryButtonParams = {
  currNation?: OptionalNation;
  isUploadRequired: boolean;
  isReceiptImageUploaded: boolean;
  unapprovedCount: number;
  nationality: string;
  isCruise?: boolean;
};

export const HOME_TOP_SECTION = {
  getLogoutModalContent: (nationality: string): LogoutModalContent => {
    const isKorean = nationality === 'KOR';

    if (isKorean) {
      return {
        title: '로그아웃 할까요?',
        content: '다른 여권번호로 조회하려면 로그아웃 하세요.',
        btnText: '로그아웃',
        subBtnText: '취소',
      };
    }

    return {
      title: 'Sign Out',
      content: `Sign out to look up\nanother passport number.`,
      btnText: 'Sign Out',
      subBtnText: 'Cancel',
    };
  },
  getLogoutButtonText: (nationality: string): string => {
    if (nationality === 'KOR') {
      return '로그아웃';
    }

    return 'Sign Out';
  },
};

export const HOME_BOTTOM_SECTION = {
  getTitle: (nationality: string): string => {
    if (nationality === 'KOR') {
      return '환급절차';
    }

    return 'Tax Refund Process';
  },
  refundInfoButton: {
    getTitle: ({
      currNation,
      isCashPickup,
    }: {
      currNation: OptionalNation;
      isCashPickup: boolean;
    }): string => {
      if (currNation === 'KOR') {
        return '정보 입력 (3개월 이내)';
      }

      if (isCashPickup) {
        return 'Enter Personal Details';
      }

      return 'Enter Remittance Info';
    },
    getDescription: ({
      currNation,
      nationality,
      isUploadRequired,
      isRefundInfoRegistered,
      isCashPickup,
    }: RefundInfoButtonParams): string => {
      const isMainNation = isNationIncluded(MAIN_NATIONS, nationality);
      if (isUploadRequired) {
        if (currNation === 'KOR') {
          return '환급액은 고객님 계좌로 지급됩니다.\n송금에 필요한 정보를 3개월 이내 작성해주세요.';
        }
        return 'The refund will be sent to your account. Please enter your payment information within 3 months after purchase.';
      }
      if (isCashPickup) {
        return 'Complete your information to receive your refund through Western Union.';
      }
      if (isMainNation) {
        return "We'll send your refund to your account.\nComplete your account information.";
      }
      return 'We will send an email within 10 days to request the information needed for processing refunds. Please check your mailbox';
    },
  },
  receiptInquiryButton: {
    getTitle: ({
      currNation,
      isUploadRequired,
    }: {
      currNation?: OptionalNation;
      isUploadRequired: boolean;
    }): string => {
      if (currNation === 'KOR') {
        return isUploadRequired ? '환급전표 출력' : '세관 방문';
      }

      return isUploadRequired ? 'Print Refund Receipt' : 'Visit customs';
    },
    getDescription: ({
      isUploadRequired,
      isReceiptImageUploaded,
      unapprovedCount,
      nationality,
    }: ReceiptInquiryButtonParams): string => {
      if (nationality === 'KOR') {
        return isReceiptImageUploaded ? '' : '환급전표를 출력하세요.';
      }

      if (isUploadRequired) {
        return isReceiptImageUploaded
          ? ''
          : 'Print your refund receipt and visit Customs.';
      }

      if (unapprovedCount === 0) {
        return '';
      }

      const isKorean = nationality === 'KOR';

      if (isKorean) {
        return '세관에 구매한 물건과 모바일 Tax Refund 영수증을 보여주세요.';
      }

      return 'Show your purchases and KTP Mobile receipt\nto customs.';
    },
  },
  uploadReceiptButton: {
    getTitle: (nationality: string, isCruise: boolean): string => {
      if (isCruise) {
        if (nationality === 'KOR') {
          return '환급 영수증 제출';
        }
        return 'Refund Receipt Submission';
      }
      if (nationality === 'KOR') {
        return '도장 받은 환급전표 사진 올리기';
      }
      return 'Upload Stamped Receipt';
    },
    getDescription: (
      isReceiptImageUploaded: boolean,
      nationality: string,
      isCruise: boolean,
    ): string => {
      if (isReceiptImageUploaded) {
        return '';
      }
      if (isCruise) {
        if (nationality === 'KOR') {
          return '세관에 제출한 환급 영수증을 확인한 후 환급액을 송금해드립니다.';
        }
        return 'Refund will be completed after we verify your Customs-submitted receipt.';
      }
      if (nationality === 'KOR') {
        return '환급전표에 세관 확인 도장을 받은 후 사진을 올려주세요.';
      }
      return 'Please upload a photo of your refund receipt stamped by Customs.';
    },
  },
  footerButtons: {
    getProcedureGuideText: (nationality: string): string => {
      if (nationality === 'KOR') {
        return '환급절차 안내';
      }

      return 'Tax Refund Process';
    },
    getLogoutText: (nationality: string): string => {
      if (nationality === 'KOR') {
        return '로그아웃';
      }

      return 'Log out';
    },
  },
};

export const HOME_CURRENT_STATUS = {
  getPreRefundTitle: (nationality: string): string => {
    if (nationality === 'KOR') {
      return '텍스 프리 영수증입니다.';
    }

    return 'Tax Refund Receipts';
  },
  getPreRefundDescription: (nationality: string): string => {
    if (nationality === 'KOR') {
      return '텍스 프리 처리가 끝난 영수증입니다.';
    }

    return `These are immediate tax-refund case.
You don't need to visit customs.`;
  },
  receiptButtons: {
    getCustomLocationTitle: (nationality: string): string => {
      if (nationality === 'KOR') {
        return '세관 위치';
      }

      return 'Custom Location';
    },
    getViewReceiptTitle: (nationality: string): string => {
      if (nationality === 'KOR') {
        return '영수증 보기';
      }

      return 'View Tax refund Receipt';
    },
  },
  korReceiptButtons: {
    getPrintMyReceiptsTitle: (nationality: string): string => {
      if (nationality === 'KOR') {
        return '영수증 출력';
      }

      return 'Print My Refund Receipts';
    },
    getCustomLocationTitle: (nationality: string): string => {
      if (nationality === 'KOR') {
        return '세관 위치';
      }

      return 'Custom Location';
    },
    getUploadReceiptTitle: (nationality: string): string => {
      if (nationality === 'KOR') {
        return '환급전표 사진 업로드';
      }

      return 'Upload Tax refund Receipt';
    },
  },
  getInputInfoTitle: (nationality: string, isCashPickup: boolean): string => {
    if (nationality === 'KOR') {
      return '계좌 정보 입력';
    }
    if (isCashPickup) {
      return 'Enter the information';
    }

    return 'Enter the information';
  },
  getViewEnteredInformationTitle: (nationality: string): string => {
    if (nationality === 'KOR') {
      return '송금 정보 확인';
    }
    return 'View Entered Information';
  },
};
